import { loadConfig } from './config';
import { getCsrfToken } from './auth';
import type { Annotation, ModelFile, Room, Sequence, WebContentOverlay } from './types';

async function base() {
  return (await loadConfig()).syncBaseURL;
}

/** Every request through here carries the session cookie + CSRF header. */
async function apiFetch(pathOrUrl: string, init: RequestInit = {}): Promise<Response> {
  const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${await base()}${pathOrUrl}`;
  const headers = new Headers(init.headers);
  const method = (init.method ?? 'GET').toUpperCase();
  const isWrite = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
  const token = getCsrfToken();
  if (isWrite && token) headers.set('X-CSRF-Token', token);
  return fetch(url, { ...init, credentials: 'include', headers });
}

async function j<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  if (!res.ok) throw new Error(`${init?.method ?? 'GET'} ${path} → HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export interface RoomAccess {
  room_id: string;
  display_name: string | null;
  visibility: 'public' | 'authenticated' | 'admin';
  closed: boolean;
  closed_at: number | null;
  createdByEmail?: string | null;
}

export const api = {
  /** The sync server's own build — separate from the Control UI's, which fetches its own /version directly (same origin). */
  async getSyncServerVersion(): Promise<{ service: string; gitSha: string; startedAt: string }> {
    return j<{ service: string; gitSha: string; startedAt: string }>(`/version`);
  },
  async listRooms(): Promise<Room[]> {
    return j<Room[]>(`/rooms`);
  },
  async listSequences(): Promise<Sequence[]> {
    return j<Sequence[]>(`/sequences`);
  },
  async listModels(): Promise<ModelFile[]> {
    return j<ModelFile[]>(`/models`);
  },
  /** A model's own default annotations — [] if none have been saved yet. */
  async getModelAnnotations(modelName: string): Promise<Annotation[]> {
    return j<Annotation[]>(`/models/${encodeURIComponent(modelName)}/annotations`);
  },
  async saveModelAnnotations(modelName: string, annotations: Annotation[]): Promise<Annotation[]> {
    return j<Annotation[]>(`/models/${encodeURIComponent(modelName)}/annotations`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(annotations),
    });
  },
  async createSequence(name: string, models: string[]) {
    return j<unknown>(`/createSequence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, models }),
    });
  },
  async updateSequence(
    fileUrl: string,
    assets: Array<{
      model: string;
      externalURL?: string;
      scaling?: string;
      position?: string;
      rotation?: string;
      webContentOverlays?: WebContentOverlay[] | null;
      annotations?: Annotation[] | null;
    }>,
  ) {
    const bare = fileUrl.replace(/^.*\//, '');
    return j<{ message: string; assets: unknown }>(
      `/updateSequence/${encodeURIComponent(bare)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assets }),
      },
    );
  },
  async deleteSequence(fileUrl: string) {
    const bare = fileUrl.replace(/^.*\//, '');
    return j<unknown>(`/deleteSequence/${encodeURIComponent(bare)}`, {
      method: 'DELETE',
    });
  },

  async uploadModels(files: File[]): Promise<{ uploaded: ModelFile[] }> {
    const fd = new FormData();
    for (const f of files) fd.append('files', f, f.name);
    const res = await apiFetch(`/uploadModels`, { method: 'POST', body: fd });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        if (body?.error) msg = body.error;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    return res.json() as Promise<{ uploaded: ModelFile[] }>;
  },

  async deleteModel(
    filename: string,
    opts?: { force?: boolean },
  ): Promise<{ message: string; usedBy?: string[] }> {
    const bare = filename.replace(/^.*\//, '');
    const path =
      `/deleteModel/${encodeURIComponent(bare)}` + (opts?.force ? '?force=1' : '');
    const res = await apiFetch(path, { method: 'DELETE' });
    const body = (await res.json().catch(() => ({}))) as {
      message?: string;
      error?: string;
      usedBy?: string[];
    };
    if (res.status === 409) {
      const err = new Error(body.error || 'Model in use') as Error & {
        code?: string;
        usedBy?: string[];
      };
      err.code = 'IN_USE';
      err.usedBy = body.usedBy;
      throw err;
    }
    if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
    return { message: body.message || 'Deleted', usedBy: body.usedBy };
  },

  // ---------- Room access / kill ----------
  async getRoomAccess(roomId: string): Promise<RoomAccess> {
    return j<RoomAccess>(`/rooms/${encodeURIComponent(roomId)}/access`);
  },
  async setRoomVisibility(
    roomId: string,
    visibility: RoomAccess['visibility'],
  ): Promise<RoomAccess> {
    return j<RoomAccess>(`/rooms/${encodeURIComponent(roomId)}/access`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visibility }),
    });
  },
  async closeRoom(roomId: string): Promise<RoomAccess & { kicked: number }> {
    return j<RoomAccess & { kicked: number }>(
      `/rooms/${encodeURIComponent(roomId)}/close`,
      { method: 'POST' },
    );
  },
  /** Invalidates the room's current join code and mints a fresh one. */
  async regenerateJoinCode(roomId: string): Promise<{ join_code: string; join_code_expires_at: number }> {
    return j<{ join_code: string; join_code_expires_at: number }>(
      `/rooms/${encodeURIComponent(roomId)}/join-code`,
      { method: 'POST' },
    );
  },

  // ---------- Users (admin) ----------
  async listUsers(): Promise<UserRow[]> {
    const { users } = await j<{ users: UserRow[] }>(`/users`);
    return users;
  },
  async updateUser(id: number, patch: Partial<Pick<UserRow, 'display_name' | 'role' | 'disabled'>>) {
    return j<{ user: UserRow; killed: number }>(`/users/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
  },
  async deleteUser(id: number) {
    return j<{ ok: true; deleted: string }>(`/users/${id}`, { method: 'DELETE' });
  },

  // ---------- Self profile ----------
  async updateProfile(patch: { display_name: string }) {
    return j<{ user: { id: number; email: string; display_name: string; role: string } }>(
      `/auth/me`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      },
    );
  },
};

export interface UserRow {
  id: number;
  email: string;
  display_name: string | null;
  role: 'admin' | 'editor' | 'viewer';
  disabled: 0 | 1;
  created_at: number;
  last_login_at: number | null;
}

/** Absolute URL to an asset served by the sync server. */
export async function assetURL(pathFromPublic: string): Promise<string> {
  const b = await base();
  const p = pathFromPublic.startsWith('/') ? pathFromPublic : `/${pathFromPublic}`;
  return `${b}${p}`;
}
